import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Tag, Plus, PencilSimple, TrashSimple, LockKey } from "@phosphor-icons/react";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

export default function ExpenseCategories() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {key?, label, icon, order}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/expense-categories`);
      setRows(data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const { sort, toggleSort, sortedRows } = useSortableRows(rows, {
    defaultKey: "order", defaultDir: "asc",
    getters: {
      order: (r) => Number(r.order) || 0,
      label: (r) => r.label || "",
      key: (r) => r.key || "",
      is_system: (r) => (r.is_system ? 1 : 0),
    },
  });

  const openNew = () => setEditing({ key: "", label: "", icon: "Tag", order: 100, _isNew: true });
  const openEdit = (r) => setEditing({ key: r.key, label: r.label, icon: r.icon || "Tag", order: r.order || 100, _isNew: false, is_system: r.is_system });

  const save = async () => {
    if (!editing.label.trim()) { toast.error("Ad zorunlu"); return; }
    setSaving(true);
    try {
      if (editing._isNew) {
        if (!editing.key.trim()) { toast.error("Anahtar zorunlu"); setSaving(false); return; }
        await axios.post(`${API}/expense-categories`, editing);
        toast.success("Kategori oluşturuldu");
      } else {
        await axios.put(`${API}/expense-categories/${editing.key}`, editing);
        toast.success("Kategori güncellendi");
      }
      setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (r.is_system) { toast.error("Sistem kategorisi silinemez"); return; }
    if (!window.confirm(`"${r.label}" kategorisini silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/expense-categories/${r.key}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Shell
      title="Masraf Kategorileri"
      subtitle="Masraf Bildirimi › Kategori Yönetimi"
      hideSiteSelector
      actions={
        <Button onClick={openNew} data-testid="cat-new"
                className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
          <Plus size={14} className="mr-1.5" /> Yeni Kategori
        </Button>
      }
    >
      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : (
        <Card className="rounded-none sm:rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">
              <tr>
                <SortableTh label="Sıra" k="order" sort={sort} onSort={toggleSort} className="text-left px-3 py-2 w-16" />
                <SortableTh label="Ad" k="label" sort={sort} onSort={toggleSort} className="text-left px-3 py-2" />
                <SortableTh label="Anahtar" k="key" sort={sort} onSort={toggleSort} className="text-left px-3 py-2 w-40" />
                <SortableTh label="Sistem" k="is_system" sort={sort} onSort={toggleSort} className="text-center px-3 py-2 w-24" align="center" />
                <th className="text-right px-3 py-2 w-32">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => (
                <tr key={r.key} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cat-row-${r.key}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.order}</td>
                  <td className="px-3 py-2 font-medium">
                    <Tag size={14} weight="duotone" className="inline mr-1.5 text-[#95C11F]" />
                    {r.label}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.key}</td>
                  <td className="px-3 py-2 text-center">
                    {r.is_system ? <LockKey size={14} className="inline text-slate-400" /> : "-"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`cat-edit-${r.key}`}
                            className="h-7 px-2"><PencilSimple size={14} /></Button>
                    {!r.is_system && (
                      <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`cat-delete-${r.key}`}
                              className="h-7 px-2 text-red-600 hover:bg-red-50"><TrashSimple size={14} /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-md rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle>{editing?._isNew ? "Yeni Kategori" : "Kategori Düzenle"}</DialogTitle>
            <DialogDescription className="text-xs">
              Kategoriler, masraf girişinde seçim listesinde çıkar.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              {editing._isNew && (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Anahtar (a-z, _)</Label>
                  <Input value={editing.key}
                         onChange={e => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                         data-testid="cat-form-key"
                         placeholder="ornek_yeni_kategori"
                         className="rounded-none sm:rounded-sm mt-1 font-mono" />
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Ad</Label>
                <Input value={editing.label} onChange={e => setEditing({ ...editing, label: e.target.value })}
                       data-testid="cat-form-label" className="rounded-none sm:rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Sıra</Label>
                <Input type="number" value={editing.order}
                       onChange={e => setEditing({ ...editing, order: Number(e.target.value) })}
                       className="rounded-none sm:rounded-sm mt-1 font-mono" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest">Vazgeç</Button>
            <Button onClick={save} disabled={saving} data-testid="cat-form-save"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
              {saving ? "…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
